import { navigate } from "gatsby"
import {
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
} from "react"

import { SvgIcon, type FC } from ".."
import { FORM_SCHEMA, type FORM_VALUES } from "./_data"
import LocationInput from "./LocationInput"

import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Drawer, useMediaQuery } from "@mui/material"
import { alpha, styled, useTheme } from "@mui/material/styles"
import { FormProvider, useForm, useWatch, type SubmitHandler } from "react-hook-form"

export type HeroSearchFormProps = {
  variant?: "default" | "home"
}

const HeroSearchForm: FC<HeroSearchFormProps> = ({ variant = "default" }) => {
  const [openDrawer, setOpenDrawer] = useState(false)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  useLayoutEffect(() => {
    if (isDesktop) setOpenDrawer(false)
  }, [isDesktop])

  return (
    <>
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Form variant={variant} />
      </Box>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <DrawerTriggerBtn onClick={() => setOpenDrawer(true)}>
          <span>Start Planning Trip</span>
          <SvgIcon variant="arrow" size={16} sx={{ transform: "rotate(180deg)" }} />
        </DrawerTriggerBtn>
      </Box>
      <Drawer
        anchor="bottom"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: (theme) => alpha(theme.palette["neutral.900"].main, 0.4),
            },
          },
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: (theme) => theme.spacing(1.5, 1.5, 0, 0),
            minHeight: "calc(100dvh - 44px)",
            mx: 3,
            p: 6,
            zIndex: 4100,
          },
        }}
        sx={{
          zIndex: 4000,
        }}
      >
        <DrawerHeader>
          <DrawerTitle>Start planning your trip</DrawerTitle>
          <DrawerCloseBtn onClick={() => setOpenDrawer(false)}>
            <SvgIcon variant="close" strokeMode size={10} />
          </DrawerCloseBtn>
        </DrawerHeader>
        <Form />
      </Drawer>
    </>
  )
}
export default HeroSearchForm

interface FormProps extends HTMLAttributes<HTMLFormElement> {
  variant?: HeroSearchFormProps["variant"]
}
const Form = (props: FormProps) => {
  const form = useForm<FORM_VALUES>({
    resolver: zodResolver(FORM_SCHEMA),
    defaultValues: {
      location: "",
    },
  })

  const location = form.watch("location")

  const onSubmit: SubmitHandler<FORM_VALUES> = (data) => {
    if (!data.location) return

    // Parse the location value to extract type and ID
    const [label, value, type] = data.location.split(":")
    
    // Navigate to trips page with the selected location
    const result = `../trips?location=${encodeURIComponent(data.location)}`
    // Note: Gatsby "navigate" does not properly handle history state even with "replace: false"
    window.history.pushState({}, "", window.location.pathname ?? "/")
    navigate(result, { replace: false })
  }

  const submitRef = useRef<HTMLButtonElement>(null)

  useLayoutEffect(() => {
    if (location) submitRef.current?.focus()
  }, [location])

  return (
    <FormProvider {...form}>
      <FormContainer onSubmit={form.handleSubmit(onSubmit)} {...props}>
        <LocationInput variant={props.variant} />
      </FormContainer>
    </FormProvider>
  )
}

const DrawerTriggerBtn = styled("button")(({ theme }) => ({
  ...theme.typography["body.lg"],
  backgroundColor: theme.palette["secondary.300"].main,
  border: 0,
  borderRadius: theme.spacing(1),
  color: theme.palette["secondary.800"].main,
  cursor: "pointer",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gridGap: theme.spacing(5),
  margin: 0,
  minHeight: 54,
  padding: theme.spacing(3.5, 5),
  width: "100%",
}))

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gridGap: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}))

const DrawerTitle = styled("h2")(({ theme }) => ({
  ...theme.typography["body.lg"],
  color: alpha(theme.palette["neutral.700"].main, 0.8),
  fontWeight: 500,
}))

const DrawerCloseBtn = styled("button")(({ theme }) => ({
  backgroundColor: theme.palette["neutral.100"].main,
  border: `1px solid ${theme.palette["neutral.200"].main}`,
  borderRadius: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  width: 40,
  height: 40,
  margin: 0,
  padding: 0,
}))

const FormContainer = styled("form")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gridGap: theme.spacing(2),
  flex: "1",
  width: "100%",

  [theme.breakpoints.up("md")]: {
    "backgroundColor": theme.palette["neutral.100"].main,
    "borderRadius": theme.spacing(1.5),
    "boxShadow": `0px 2px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
    "overflow": "hidden",
  },
}))

const SubmitInput = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2, 6),

  [theme.breakpoints.down("md")]: {
    padding: 0,
    marginTop: "auto",
  },
}))

type SubmitBtnProps = {
  isReady?: boolean
  variant?: string
}

const SubmitBtn = styled("button")<SubmitBtnProps>(({ isReady = false, variant, theme }) => ({
  ...theme.typography["body.md"],
  "letterSpacing": 0,
  "fontWeight": 500,
  "color": theme.palette["secondary.800"].main,
  "cursor": "pointer",
  "background":
    variant === "home" ? theme.palette["primary.800"].main : theme.palette["secondary.300"].main,
  "border": 0,
  "boxShadow": `
    0px 1px 2px 0px ${alpha(theme.palette["secondary.800"].main, 0.2)},
    0px 4px 6px -1px ${alpha(theme.palette["neutral.900"].main, 0.04)}, 
    0px 2px 14px -1px ${alpha(theme.palette["neutral.900"].main, 0.04)}`,
  "borderRadius": theme.spacing(1),
  "margin": 0,
  "padding": theme.spacing(3.5, 5),
  "position": "relative",
  "height": 54,
  "width": "100%",
  "transition": theme.transitions.create(["background-color", "color", "padding", "width"]),
  ...(variant === "home" && {
    background: theme.palette["primary.800"].main,
    color: theme.palette["neutral.100"].main,
  }),

  "&:hover, &:focus": {
    background: theme.palette["secondary.200"].main,
    color: theme.palette["secondary.800"].main,
  },

  [theme.breakpoints.up("md")]: {
    "borderRadius": 9999,
    "boxShadow": "none",
    "height": 52,
    "padding": theme.spacing(5),
    "overflow": "hidden",
    "width": isReady ? 124 : 52,

    "& > span": {
      position: "absolute",
      top: "50%",
      right: 52,
      transform: "translate(0, -50%)",
    },

    "& > svg": {
      position: "absolute",
      width: 20,
      height: 20,
      top: "50%",
      right: 16,
      transform: "translate(0, -50%) rotate(180deg)",
    },

    "&:hover, &:focus": {
      "background": theme.palette["secondary.200"].main,
      "color": theme.palette["secondary.800"].main,
      "padding": theme.spacing(2, 7, 2, 3),
      "width": 124,
      ...(variant === "home" && {
        background: theme.palette["primary.800"].main,
        color: theme.palette["neutral.100"].main,
      }),

      "& > svg": {
        fill: theme.palette["secondary.800"].main,
        color: theme.palette["secondary.800"].main,
        ...(variant === "home" && {
          fill: theme.palette["neutral.100"].main,
          color: theme.palette["neutral.100"].main,
        }),
      },
    },
  },
}))

interface SubmitButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: string
}
const SubmitButton = forwardRef<HTMLButtonElement, SubmitButtonProps>((props, ref) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))
  // Watch the form values to determine if the submit button is ready
  const [location] = useWatch({
    name: ["location"],
  })
  const isReady = location !== ""

  return (
    <SubmitBtn ref={ref} isReady={isReady} {...props}>
      {isDesktop ? (
        <>
          <span>Search</span>
          <SvgIcon variant="arrow" size={20} />
        </>
      ) : (
        <span>Find My Trip</span>
      )}
    </SubmitBtn>
  )
})
SubmitButton.displayName = "SubmitButton"
